/**
 * MatchDetails Page - Detailed match analysis with explanation
 * Phase 5 Part 2: Recommendations Dashboard UI
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import {
  useMatchDetails,
  useMatchExplanation,
  MATCH_QUALITY_LABELS
} from '../../hooks/useMatching';
import BuyerIntelligence from '../../components/BuyerIntelligence';
import { ConversationThread } from '../../components/ConversationThread';
import { messagingService } from '../../services/messagingService';
import { buyersAPI } from '../../services/api';
import {
  ArrowLeft,
  RefreshCw,
  Building2,
  TrendingUp,
  TrendingDown,
  Minus,
  Send,
  Eye,
  Clock,
  ExternalLink,
  Calendar,
  Activity,
  Target,
  Zap,
  Info,
  CheckCircle,
  AlertCircle,
  Brain,
  Lightbulb,
  TrendingUp as Rocket,
  MessageSquare,
  X,
  Loader2
} from 'lucide-react';

// Score component visualization
function ScoreComponent({ label, score, weight, color, description }) {
  const weightPercent = Math.round(weight * 100);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h4 className="font-medium text-gray-900">{label}</h4>
          <p className="text-xs text-gray-500 mt-0.5">{weightPercent}% of total score</p>
        </div>
        <div
          className="text-2xl font-bold"
          style={{ color }}
        >
          {Math.round(score)}
        </div>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${score}%`, backgroundColor: color }}
        />
      </div>
      {description && (
        <p className="text-sm text-gray-600 mt-3">{description}</p>
      )}
    </div>
  );
}

// Recommendation box
function RecommendationBox({ action, timing, score, activityScore, marketOpportunityScore, onAction, buyerName, bdPageUrl }) {
  // Hot = high score + either high market opportunity or signals
  const isHot = score >= 55 && (marketOpportunityScore >= 40 || activityScore >= 30);

  const actionConfig = {
    reach_out: {
      icon: Send,
      title: 'Reach Out Now',
      description: 'High match score combined with active buyer interest. This is an ideal time to make contact.',
      className: 'bg-green-50 border-green-200 text-green-800',
      buttonClass: 'bg-green-600 hover:bg-green-700 text-white'
    },
    monitor: {
      icon: Eye,
      title: 'Monitor Activity',
      description: 'Strong match but limited recent activity. Keep an eye on this buyer for engagement signals.',
      className: 'bg-blue-50 border-blue-200 text-blue-800',
      buttonClass: 'bg-blue-600 hover:bg-blue-700 text-white'
    },
    wait: {
      icon: Clock,
      title: 'Add to Watch List',
      description: 'Moderate match score. Continue building your profile and monitor for changes.',
      className: 'bg-yellow-50 border-yellow-200 text-yellow-800',
      buttonClass: 'bg-yellow-600 hover:bg-yellow-700 text-white'
    },
    low_priority: {
      icon: Target,
      title: 'Low Priority',
      description: 'Limited match potential at this time. Focus on higher priority opportunities.',
      className: 'bg-gray-50 border-gray-200 text-gray-700',
      buttonClass: 'bg-gray-600 hover:bg-gray-700 text-white'
    }
  };

  const config = actionConfig[action] || actionConfig.low_priority;
  const Icon = config.icon;

  const timingLabels = {
    immediate: 'Act within 24 hours',
    this_week: 'Take action this week',
    this_month: 'Review this month',
    not_urgent: 'No immediate action needed'
  };

  return (
    <div className={`rounded-xl border-2 p-6 ${config.className}`}>
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center flex-shrink-0">
          <Icon className="w-6 h-6" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold">{config.title}</h3>
            {isHot && (
              <span className="flex items-center gap-1 px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full">
                <Zap className="w-3 h-3" /> Hot Lead
              </span>
            )}
          </div>
          <p className="text-sm mt-1 opacity-80">{config.description}</p>
          <p className="text-xs mt-2 font-medium">{timingLabels[timing] || 'Review when available'}</p>
        </div>
      </div>
      <div className="mt-4 flex gap-3">
        <button
          onClick={() => {
            // Always open BD page if available for reach_out or monitor actions
            if ((action === 'reach_out' || action === 'monitor') && bdPageUrl) {
              window.open(bdPageUrl, '_blank');
            } else if (bdPageUrl) {
              // For any other action with BD page, still offer to open it
              window.open(bdPageUrl, '_blank');
            } else if (onAction) {
              onAction(action);
            } else {
              alert(`No BD contact page available for ${buyerName || 'this buyer'}. Check back later.`);
            }
          }}
          className={`flex-1 py-2 px-4 rounded-lg font-medium ${config.buttonClass}`}
        >
          {bdPageUrl ? 'Visit BD Page' : 'Take Action'}
        </button>
        <button
          onClick={() => {
            // Simple reminder - could integrate with calendar API later
            const reminderDate = new Date();
            if (timing === 'immediate') reminderDate.setDate(reminderDate.getDate() + 1);
            else if (timing === 'this_week') reminderDate.setDate(reminderDate.getDate() + 7);
            else if (timing === 'this_month') reminderDate.setDate(reminderDate.getDate() + 30);
            else reminderDate.setDate(reminderDate.getDate() + 14);

            const title = `Follow up with ${buyerName || 'Buyer'}`;
            const googleCalUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&dates=${reminderDate.toISOString().replace(/[-:]/g, '').split('.')[0]}Z/${reminderDate.toISOString().replace(/[-:]/g, '').split('.')[0]}Z`;
            window.open(googleCalUrl, '_blank');
          }}
          className="py-2 px-4 rounded-lg font-medium bg-white border border-current hover:bg-gray-50"
        >
          Schedule Reminder
        </button>
      </div>
    </div>
  );
}

export default function MatchDetails() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();

  // State for floating message window
  const [showMessageWindow, setShowMessageWindow] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [loadingConversation, setLoadingConversation] = useState(false);

  // State for signal count (fetched from intelligence API)
  const [signalCount, setSignalCount] = useState(0);

  // Parse matchId - could be UUID or "sellerId___buyerId" (triple underscore separator)
  const [sellerId, buyerId] = matchId?.includes('___')
    ? matchId.split('___')
    : [user?.sellerId, matchId];

  // Get sellerId from userProfile if not in matchId
  const actualSellerId = sellerId || userProfile?.seller_id;

  // Fetch match data
  const {
    match,
    loading: matchLoading,
    error: matchError,
    recalculate
  } = useMatchDetails(sellerId, buyerId);

  const {
    explanation,
    loading: explanationLoading
  } = useMatchExplanation(sellerId, buyerId);

  // Fetch signal count from intelligence API
  useEffect(() => {
    if (!buyerId) return;

    const fetchSignalCount = async () => {
      try {
        const response = await buyersAPI.getIntelligence(buyerId);
        if (response.data?.success) {
          const signals = response.data.data?.recent_signals || [];
          setSignalCount(signals.length);
        }
      } catch (err) {
        console.error('Failed to fetch signal count:', err);
      }
    };

    fetchSignalCount();
  }, [buyerId]);

  // Handle recalculate
  const handleRecalculate = async () => {
    await recalculate('manual');
  };

  // Handle open message window
  const handleOpenMessage = async () => {
    if (!actualSellerId || !buyerId) return;

    setLoadingConversation(true);
    setShowMessageWindow(true);

    try {
      const result = await messagingService.getOrCreateConversation(actualSellerId, buyerId);
      const convId = result?.id || result;
      setConversationId(convId);
    } catch (err) {
      console.error('Error creating conversation:', err);
    } finally {
      setLoadingConversation(false);
    }
  };

  // Loading state
  if (matchLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-500">Loading match details...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (matchError || !match) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Match Not Found</h2>
        <p className="text-gray-500 mb-6">The match you're looking for doesn't exist or has been removed.</p>
        <Link to="/seller/matching" className="btn-primary">
          Back to Matches
        </Link>
      </div>
    );
  }

  // Get quality info
  const quality = match.match_quality || 'moderate';
  const qualityInfo = MATCH_QUALITY_LABELS[quality] || MATCH_QUALITY_LABELS.moderate;
  const score = match.final_score || match.match_score || 0;
  const scoreChange = match.score_change || 0;

  // Buyer info
  const buyer = match.buyers || {};
  const buyerName = buyer.company_name || 'Unknown Company';

  return (
    <div className="max-w-5xl mx-auto space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Matches
        </button>
        <div className="flex items-center gap-2">
          <button
            onClick={handleOpenMessage}
            className="btn-primary flex items-center gap-2"
          >
            <MessageSquare className="w-4 h-4" />
            Send Message
          </button>
          <button
            onClick={handleRecalculate}
            className="btn-secondary flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Recalculate
          </button>
        </div>
      </div>

      {/* Main Score Card */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-start gap-6">
            {/* Company Logo/Icon */}
            <div className="w-20 h-20 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
              {buyer.logo_url ? (
                <img src={buyer.logo_url} alt={buyerName} className="w-14 h-14 object-contain" />
              ) : (
                <Building2 className="w-10 h-10 text-gray-400" />
              )}
            </div>

            {/* Company Info */}
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900">{buyerName}</h1>
              <p className="text-gray-500 mt-1">{buyer.headquarters}</p>
              {buyer.stock_ticker && (
                <span className="inline-block mt-2 px-2 py-1 bg-gray-100 text-gray-600 text-sm rounded">
                  {buyer.stock_ticker}
                </span>
              )}
            </div>

            {/* Score */}
            <div className="text-right">
              <div
                className="text-5xl font-bold"
                style={{ color: qualityInfo.color === 'green' ? '#10B981' : qualityInfo.color === 'blue' ? '#3B82F6' : qualityInfo.color === 'yellow' ? '#F59E0B' : '#6B7280' }}
              >
                {Math.round(score)}
              </div>
              <div className="flex items-center justify-end gap-2 mt-2">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  quality === 'excellent' ? 'bg-green-100 text-green-800' :
                  quality === 'strong' ? 'bg-blue-100 text-blue-800' :
                  quality === 'moderate' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {qualityInfo.label}
                </span>
                {scoreChange !== 0 && (
                  <span className={`flex items-center gap-1 text-sm ${
                    scoreChange > 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {scoreChange > 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                    {Math.abs(Math.round(scoreChange))}
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-400 mt-2">
                {match.confidence ? `${Math.round(match.confidence * 100)}% confidence` : ''}
              </p>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-4 divide-x divide-gray-100 bg-gray-50">
          <div className="p-4 text-center">
            <p className="text-2xl font-bold text-gray-900">{signalCount}</p>
            <p className="text-xs text-gray-500">Signals</p>
          </div>
          <div className="p-4 text-center">
            <p className="text-2xl font-bold text-gray-900">{match.profile_view_count || 0}</p>
            <p className="text-xs text-gray-500">Profile Views</p>
          </div>
          <div className="p-4 text-center">
            <p className="text-2xl font-bold text-gray-900">
              {match.last_interaction_date
                ? new Date(match.last_interaction_date).toLocaleDateString()
                : 'Never'}
            </p>
            <p className="text-xs text-gray-500">Last Interaction</p>
          </div>
          <div className="p-4 text-center">
            <p className="text-2xl font-bold text-gray-900">
              {match.calculated_at
                ? new Date(match.calculated_at).toLocaleDateString()
                : '-'}
            </p>
            <p className="text-xs text-gray-500">Last Calculated</p>
          </div>
        </div>
      </div>

      {/* Recommendation */}
      <RecommendationBox
        action={match.recommended_action || 'low_priority'}
        timing={match.recommended_timing || 'not_urgent'}
        score={score}
        activityScore={match.activity_score || 0}
        marketOpportunityScore={match.market_opportunity_score || 0}
        buyerName={buyerName}
        bdPageUrl={buyer.bd_page_url}
      />

      {/* Score Breakdown */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-6">
          <Info className="w-5 h-5 text-gray-400" />
          <h2 className="text-lg font-semibold text-gray-900">Score Breakdown</h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          <ScoreComponent
            label="Therapeutic Areas"
            score={match.therapeutic_match_score || 0}
            weight={0.30}
            color="#8B5CF6"
            description={explanation?.breakdown?.therapeutic_areas?.description}
          />
          <ScoreComponent
            label="Buyer Need"
            score={match.buyer_need_score || 0}
            weight={0.20}
            color="#EC4899"
            description={explanation?.breakdown?.buyer_need?.description}
          />
          <ScoreComponent
            label="Technology Platform"
            score={match.platform_match_score || 0}
            weight={0.12}
            color="#3B82F6"
            description={explanation?.breakdown?.tech_platforms?.description}
          />
          <ScoreComponent
            label="Development Stage"
            score={match.stage_match_score || 0}
            weight={0.12}
            color="#10B981"
            description={explanation?.breakdown?.development_stage?.description}
          />
          <ScoreComponent
            label="Market Opportunity"
            score={match.market_opportunity_score || 0}
            weight={0.12}
            color="#F59E0B"
            description={explanation?.breakdown?.market_opportunity?.description}
          />
          <ScoreComponent
            label="Buyer Activity"
            score={match.activity_score || 0}
            weight={0.14}
            color="#EF4444"
            description={explanation?.breakdown?.buyer_activity?.description}
          />
        </div>
      </div>

      {/* Market Opportunity Insights */}
      {match.market_opportunity_insights?.length > 0 && (
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Lightbulb className="w-5 h-5 text-amber-600" />
            <h2 className="text-lg font-semibold text-gray-900">Market Opportunity Insights</h2>
            <span className="ml-auto px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full font-medium">
              Signal-Based Analysis
            </span>
          </div>

          <div className="space-y-3">
            {match.market_opportunity_insights.map((insight, i) => (
              <div key={i} className="flex items-start gap-3 bg-white/70 rounded-lg p-3">
                <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Rocket className="w-3.5 h-3.5 text-amber-600" />
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">{insight}</p>
              </div>
            ))}
          </div>

          {/* Market Opportunity Score Components */}
          {match.market_opportunity_components && (
            <div className="mt-4 pt-4 border-t border-amber-200">
              <p className="text-xs text-gray-500 mb-3">Score Components</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-white/80 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-red-600">{match.market_opportunity_components.pipelineGapUrgency || 0}</p>
                  <p className="text-xs text-gray-500">Pipeline Gap</p>
                  <p className="text-[10px] text-gray-400">of 30 pts</p>
                </div>
                <div className="bg-white/80 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-purple-600">{match.market_opportunity_components.acquisitionAppetite || 0}</p>
                  <p className="text-xs text-gray-500">Acquisition Appetite</p>
                  <p className="text-[10px] text-gray-400">of 25 pts</p>
                </div>
                <div className="bg-white/80 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-blue-600">{match.market_opportunity_components.strategicAlignment || 0}</p>
                  <p className="text-xs text-gray-500">Strategic Fit</p>
                  <p className="text-[10px] text-gray-400">of 25 pts</p>
                </div>
                <div className="bg-white/80 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-green-600">{match.market_opportunity_components.financialCapacity || 0}</p>
                  <p className="text-xs text-gray-500">Financial Capacity</p>
                  <p className="text-[10px] text-gray-400">of 20 pts</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Matching Details */}
      {(match.matching_therapeutic_areas?.length > 0 || match.matching_tech_platforms?.length > 0) && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <h2 className="text-lg font-semibold text-gray-900">What Matches</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {match.matching_therapeutic_areas?.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Therapeutic Areas</h4>
                <div className="flex flex-wrap gap-2">
                  {match.matching_therapeutic_areas.map((area, i) => (
                    <span key={i} className="px-3 py-1.5 bg-purple-50 text-purple-700 rounded-lg text-sm">
                      {area}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {match.matching_tech_platforms?.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Technology Platforms</h4>
                <div className="flex flex-wrap gap-2">
                  {match.matching_tech_platforms.map((platform, i) => (
                    <span key={i} className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-sm">
                      {platform}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {match.matching_keywords?.length > 0 && (
              <div className="md:col-span-2">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Matching Keywords</h4>
                <div className="flex flex-wrap gap-2">
                  {match.matching_keywords.map((keyword, i) => (
                    <span key={i} className="px-3 py-1.5 bg-gray-50 text-gray-700 rounded-lg text-sm">
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Buyer Intelligence */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Brain className="w-5 h-5 text-indigo-500" />
          <h2 className="text-lg font-semibold text-gray-900">Buyer Intelligence & Signals</h2>
        </div>
        <BuyerIntelligence buyerId={buyerId} />
      </div>

      {/* Additional Info */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Additional Details</h2>

        <dl className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <dt className="text-sm text-gray-500">Match Quality</dt>
            <dd className="font-medium text-gray-900 mt-1">{qualityInfo.label}</dd>
          </div>
          <div>
            <dt className="text-sm text-gray-500">Score Trend</dt>
            <dd className="font-medium text-gray-900 mt-1 flex items-center gap-1">
              {match.score_trend === 'improving' && <TrendingUp className="w-4 h-4 text-green-500" />}
              {match.score_trend === 'declining' && <TrendingDown className="w-4 h-4 text-red-500" />}
              {match.score_trend === 'stable' && <Minus className="w-4 h-4 text-gray-400" />}
              {match.score_trend || 'Stable'}
            </dd>
          </div>
          <div>
            <dt className="text-sm text-gray-500">Data Freshness</dt>
            <dd className="font-medium text-gray-900 mt-1">
              {match.data_freshness || 0} days old
            </dd>
          </div>
          <div>
            <dt className="text-sm text-gray-500">First Calculated</dt>
            <dd className="font-medium text-gray-900 mt-1">
              {match.first_calculated_at
                ? new Date(match.first_calculated_at).toLocaleDateString()
                : '-'}
            </dd>
          </div>
        </dl>
      </div>

      {/* Actions */}
      <div className="flex gap-4">
        <Link
          to="/seller/matching"
          className="flex-1 py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium text-center transition-colors"
        >
          Back to All Matches
        </Link>
        {buyer.bd_page_url && (
          <a
            href={buyer.bd_page_url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 py-3 px-4 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium text-center transition-colors flex items-center justify-center gap-2"
          >
            Visit BD Page
            <ExternalLink className="w-4 h-4" />
          </a>
        )}
      </div>

      {/* Floating Message Window */}
      {showMessageWindow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 transition-opacity"
            onClick={() => setShowMessageWindow(false)}
          />

          {/* Message Window */}
          <div className="relative w-full max-w-2xl h-[80vh] max-h-[700px] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                  {buyer.logo_url ? (
                    <img src={buyer.logo_url} alt={buyerName} className="w-8 h-8 object-contain rounded-full" />
                  ) : (
                    <Building2 className="w-5 h-5 text-purple-600" />
                  )}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{buyerName}</h3>
                  <p className="text-sm text-gray-500">Match Score: {Math.round(score)}</p>
                </div>
              </div>
              <button
                onClick={() => setShowMessageWindow(false)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Conversation Thread */}
            <div className="flex-1 overflow-hidden">
              {loadingConversation ? (
                <div className="h-full flex flex-col items-center justify-center">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-600 mb-4" />
                  <p className="text-gray-600">Starting conversation...</p>
                </div>
              ) : conversationId ? (
                <ConversationThread
                  conversationId={conversationId}
                  userType="seller"
                  onBack={() => setShowMessageWindow(false)}
                  showHeader={false}
                  className="h-full"
                />
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-gray-500">
                  <AlertCircle className="w-8 h-8 mb-2" />
                  <p>Unable to start conversation</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
